/**
 * Cart Drawer Quick Add Overlay
 *
 * Bottom-sheet dialog for selecting variant options (color + size) on multi-variant
 * upsell products, then adding to cart. Follows the same HTMLElement + fetch +
 * CustomEvent pattern as CartDrawerFreeGiftModal.
 */


import { announceCartLinesUpdate } from '@theme/cart-events';
class CartDrawerQuickAdd extends HTMLElement {
  /** @type {HTMLDialogElement|null} */
  #dialog = null;

  /** @type {Object|null} */
  #productData = null;

  /** @type {Object} selected option values keyed by option name */
  #selectedOptions = {};

  /** @type {number|null} */
  #selectedVariantId = null;

  /** @type {boolean} */
  #isAdding = false;

  /** @type {string} the CTA's server-rendered label, captured before any "Adding…" swap */
  #ctaLabel = 'Add to Cart';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');

    if (!this.#dialog) {
      return;
    }

    this.#dialog.addEventListener('click', this.#handleDialogClick.bind(this));
    this.#dialog.addEventListener('cancel', this.#handleCancel.bind(this));

    const closeBtn = this.querySelector('[ref="closeBtn"]');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    const ctaBtn = this.querySelector('[ref="ctaBtn"]');

    if (ctaBtn) {
      this.#ctaLabel = ctaBtn.textContent.trim() || this.#ctaLabel;
      ctaBtn.addEventListener('click', () => this.#addToCart());
    }
  }

  /**
   * Open the overlay and populate it with product data.
   * @param {Object} productData
   * @param {string} productData.title
   * @param {string} productData.url
   * @param {string} productData.featuredImage
   * @param {string} productData.price - formatted price string
   * @param {string} [productData.compareAtPrice] - formatted compare-at price
   * @param {Array} productData.options - [{name, values}]
   * @param {Array} productData.variants - [{id, available, options, image, swatchColor, swatchImage}]
   */
  open(productData) {
    if (!this.#dialog) {
      return;
    }

    this.#productData = productData;
    this.#selectedOptions = {};
    this.#selectedVariantId = null;

    // A successful add leaves the button reading "Adding…" (the sheet is closing, so nobody sees
    // it) — reset it here, or the NEXT product opened from "You may also like" shows a sheet whose
    // CTA already says "Adding…" and looks stuck before it has been clicked.
    this.#resetCta();

    this.#populateHeader();
    this.#populateOptions();
    this.#selectFirstAvailable();

    this.#dialog.showModal();
  }

  close() {
    if (!this.#dialog || !this.#dialog.open) {
      return;
    }

    this.#dialog.classList.add('dialog-closing');

    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;

      clearTimeout(fallback);
      this.#dialog.classList.remove('dialog-closing');
      this.#dialog.close();
      this.#dialog.removeEventListener('animationend', finish);
    };

    // `animationend` is the happy path, but it is not guaranteed: a panel that is not rendering an
    // animation (reduced-motion overrides, a display change mid-flight, a second close() while the
    // first is still running) never fires it, and the sheet would then sit open forever with its
    // CTA stuck on "Adding…". The timer is the floor — 250ms is the slide-down duration.
    const fallback = setTimeout(finish, 300);

    this.#dialog.addEventListener('animationend', finish);
  }

  /**
   * Closes the sheet immediately, with no slide-down animation.
   *
   * Used on the success path of #addToCart, mirroring `cart-drawer-edit-item.js#closeNow`: the
   * animated close() defers the real `dialog.close()` to an `animationend` event, and the cart
   * re-render lands in that window — the shopper was left looking at an open sheet whose CTA read
   * "Adding…" for an add that had already gone through.
   */
  #closeNow() {
    if (!this.#dialog) {
      return;
    }

    this.#dialog.classList.remove('dialog-closing');

    if (this.#dialog.open) {
      this.#dialog.close();
    }
  }

  /**
   * Put the CTA back to its pristine label and enabled state.
   */
  #resetCta() {
    const ctaBtn = this.querySelector('[ref="ctaBtn"]');

    if (!ctaBtn) return;

    ctaBtn.textContent = this.#ctaLabel;
    ctaBtn.classList.remove('is-loading');
    ctaBtn.disabled = true;
  }

  #populateHeader() {
    const imgEl = this.querySelector('[ref="productImage"]');
    const titleEl = this.querySelector('[ref="productTitle"]');
    const priceEl = this.querySelector('[ref="productPrice"]');
    const comparePriceEl = this.querySelector('[ref="comparePrice"]');

    if (imgEl && this.#productData.featuredImage) {
      imgEl.src = this.#productData.featuredImage;
      imgEl.alt = this.#productData.title || '';
      imgEl.hidden = false;
    } else if (imgEl) {
      // Drop the attribute rather than setting src=''. An empty src is still a request the browser
      // fails, so it paints its broken-image glyph in place of the thumbnail.
      imgEl.removeAttribute('src');
      imgEl.alt = '';
      imgEl.hidden = true;
    }

    if (titleEl) {
      titleEl.textContent = this.#productData.title || '';
    }

    if (priceEl) {
      priceEl.textContent = this.#productData.price || '';
    }

    if (comparePriceEl) {
      if (this.#productData.compareAtPrice) {
        comparePriceEl.textContent = this.#productData.compareAtPrice;
        comparePriceEl.removeAttribute('hidden');
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.setAttribute('hidden', '');
      }
    }
  }

  #populateOptions() {
    const colorSection = this.querySelector('[ref="colorSection"]');
    const sizeSection = this.querySelector('[ref="sizeSection"]');
    const swatchesWrap = this.querySelector('[ref="swatchesWrap"]');
    const sizesWrap = this.querySelector('[ref="sizesWrap"]');
    const colorLabel = this.querySelector('[ref="colorLabel"]');
    const sizeLabel = this.querySelector('[ref="sizeLabel"]');

    if (colorSection) {
      colorSection.style.display = 'none';
    }

    if (sizeSection) {
      sizeSection.style.display = 'none';
    }

    if (swatchesWrap) {
      swatchesWrap.innerHTML = '';
    }

    if (sizesWrap) {
      sizesWrap.innerHTML = '';
    }

    if (!this.#productData.options || this.#productData.options.length === 0) {
      return;
    }

    // Exactly ONE colour option and ONE size option — see the matching comment in
    // assets/cart-drawer-edit-item.js. `#isColorOption` also matches 'style', and the containers
    // are cleared once above the loop, so two matching options doubled the rendered control count.
    const colorOptionName = this.#productData.options.find((option) => this.#isColorOption(option.name))?.name;
    let sizeRendered = false;

    for (const option of this.#productData.options) {
      const isColor = option.name === colorOptionName;

      if (!isColor) {
        if (sizeRendered) continue;
        sizeRendered = true;
      }

      if (isColor && colorSection && swatchesWrap) {
        if (colorLabel) {
          colorLabel.innerHTML = `${option.name}: <strong ref="colorValue"></strong>`;
        }

        for (const value of option.values) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cart-drawer-quick-add__swatch';
          btn.dataset.optionName = option.name;
          btn.dataset.optionValue = value;
          btn.setAttribute('aria-label', value);
          btn.title = value;
          btn.style.setProperty('--swatch-bg', this.#getSwatchBackground(value));

          btn.addEventListener('click', () => {
            this.#selectOption(option.name, value);
          });

          swatchesWrap.appendChild(btn);
        }

        colorSection.style.display = '';
      } else if (sizeSection && sizesWrap) {
        if (sizeLabel) {
          sizeLabel.innerHTML = `${option.name}: <strong ref="sizeValue"></strong>`;
        }

        for (const value of option.values) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cart-drawer-quick-add__size';
          btn.dataset.optionName = option.name;
          btn.dataset.optionValue = value;
          btn.textContent = value;

          btn.addEventListener('click', () => {
            this.#selectOption(option.name, value);
          });

          sizesWrap.appendChild(btn);
        }

        sizeSection.style.display = '';
      }
    }
  }

  #selectFirstAvailable() {
    if (!this.#productData || !this.#productData.variants) {
      return;
    }

    const firstAvailable = this.#productData.variants.find((v) => v.available);

    if (!firstAvailable) {
      return;
    }

    for (const [optionName, value] of Object.entries(firstAvailable.options)) {
      this.#selectedOptions[optionName] = value;
    }

    this.#updateUI();
  }

  /**
   * @param {string} optionName
   * @param {string} value
   */
  #selectOption(optionName, value) {
    this.#selectedOptions[optionName] = value;
    this.#updateUI();
  }

  #updateUI() {
    this.#updateVariant();
    this.#updateSwatchSelection();
    this.#updateSizeSelection();
    this.#updateProductImage();
    this.#updateOptionLabels();
    this.#updatePrice();
    this.#updateCta();
  }

  #updateVariant() {
    if (!this.#productData || !this.#productData.variants) {
      this.#selectedVariantId = null;
      return;
    }

    const match = this.#productData.variants.find((v) => {
      for (const [key, val] of Object.entries(this.#selectedOptions)) {
        if (v.options[key] !== val) {
          return false;
        }
      }

      return true;
    });

    if (match && match.available) {
      this.#selectedVariantId = match.id;
    } else {
      this.#selectedVariantId = null;
    }
  }

  #updateSwatchSelection() {
    const swatches = this.querySelectorAll('.cart-drawer-quick-add__swatch');

    for (const swatch of swatches) {
      const optionName = swatch.dataset.optionName;
      const optionValue = swatch.dataset.optionValue;
      const isSelected = this.#selectedOptions[optionName] === optionValue;

      swatch.classList.toggle('cart-drawer-quick-add__swatch--selected', isSelected);

      const isAvailable = this.#isOptionValueAvailable(optionName, optionValue);
      swatch.classList.toggle('cart-drawer-quick-add__swatch--unavailable', !isAvailable);
    }
  }

  #updateSizeSelection() {
    const sizes = this.querySelectorAll('.cart-drawer-quick-add__size');

    for (const size of sizes) {
      const optionName = size.dataset.optionName;
      const optionValue = size.dataset.optionValue;
      const isSelected = this.#selectedOptions[optionName] === optionValue;

      size.classList.toggle('cart-drawer-quick-add__size--selected', isSelected);

      const isAvailable = this.#isOptionValueAvailable(optionName, optionValue);
      size.classList.toggle('cart-drawer-quick-add__size--unavailable', !isAvailable);
    }
  }

  #updateProductImage() {
    const imgEl = this.querySelector('[ref="productImage"]');

    if (!imgEl || !this.#productData) {
      return;
    }

    const colorOption = this.#productData.options.find((o) => this.#isColorOption(o.name));

    if (!colorOption) {
      return;
    }

    const selectedColor = this.#selectedOptions[colorOption.name];

    if (!selectedColor) {
      return;
    }

    const matchingVariant = this.#productData.variants.find(
      (v) => v.options[colorOption.name] === selectedColor && v.image
    );

    const nextSrc = matchingVariant?.image || this.#productData.featuredImage;

    if (!nextSrc) {
      return;
    }

    imgEl.src = nextSrc;
    // #populateHeader hides the thumbnail when the product JSON carried no featured image; a
    // variant image resolved here has to un-hide it.
    imgEl.alt = this.#productData.title || '';
    imgEl.hidden = false;
  }

  #updateOptionLabels() {
    for (const option of this.#productData?.options || []) {
      const isColor = this.#isColorOption(option.name);
      const refKey = isColor ? 'colorValue' : 'sizeValue';
      const el = this.querySelector(`[ref="${refKey}"]`);

      if (el) {
        el.textContent = this.#selectedOptions[option.name] || '';
      }
    }
  }

  #updatePrice() {
    const priceEl = this.querySelector('[ref="productPrice"]');
    const comparePriceEl = this.querySelector('[ref="comparePrice"]');

    if (!priceEl || !this.#productData) {
      return;
    }

    const match = this.#productData.variants.find((v) => {
      for (const [key, val] of Object.entries(this.#selectedOptions)) {
        if (v.options[key] !== val) {
          return false;
        }
      }

      return true;
    });

    if (match && match.price) {
      priceEl.textContent = match.price;
    } else {
      priceEl.textContent = this.#productData.price || '';
    }

    if (comparePriceEl) {
      if (match && match.compareAtPrice) {
        comparePriceEl.textContent = match.compareAtPrice;
        comparePriceEl.removeAttribute('hidden');
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.setAttribute('hidden', '');
      }
    }
  }

  #updateCta() {
    const ctaBtn = this.querySelector('[ref="ctaBtn"]');

    if (ctaBtn) {
      ctaBtn.disabled = !this.#selectedVariantId || this.#isAdding;
      if (!this.#isAdding) ctaBtn.textContent = this.#selectedVariantId ? this.#ctaLabel : 'Sold out';
    }
  }

  /**
   * @param {string} optionName
   * @param {string} optionValue
   * @returns {boolean}
   */
  #isOptionValueAvailable(optionName, optionValue) {
    if (!this.#productData || !this.#productData.variants) {
      return false;
    }

    // Sizes follow Shopify's earlier-options rule; colours also respect the selected size, as the PDP does.
    // Mirrors assets/cart-drawer-edit-item.js — keep the two in sync.
    const optionOrder = (this.#productData.options || []).map((option) => option.name);
    const optionIndex = optionOrder.indexOf(optionName);
    const constrainingOptions = this.#isColorOption(optionName)
      ? optionOrder.filter((name) => name !== optionName)
      : optionOrder.slice(0, Math.max(optionIndex, 0));

    return this.#productData.variants.some((v) => {
      if (!v.available) {
        return false;
      }

      if (v.options[optionName] !== optionValue) {
        return false;
      }

      return constrainingOptions.every((name) => {
        const selected = this.#selectedOptions[name];
        return selected === undefined || v.options[name] === selected;
      });
    });
  }

  /**
   * @param {string} name
   * @returns {boolean}
   */
  #isColorOption(name) {
    const lower = name.toLowerCase();
    return lower === 'color' || lower === 'colour' || lower === 'style';
  }

  /**
   * Resolves a colour value to a CSS `background` shorthand — a hex, a `linear-gradient(…)` or a
   * `url(…) center/cover no-repeat`. Kept in sync with
   * `assets/cart-drawer-edit-item.js#getSwatchBackground`.
   *
   * @param {string} colorValue
   * @returns {string}
   */
  #getSwatchBackground(colorValue) {
    const info = this.#lookupSwatch(colorValue);

    if (typeof info === 'string' && info) return info;
    if (info?.image) return `url('${info.image}') center/cover no-repeat`;
    if (info?.color) return info.color;

    // Unresolved colours paint with the colour NAME as a CSS colour, then a neutral grey. The old
    // unconditional #f3f3f3 was near-invisible on the panel's white background, so every miss read
    // as a blank disc. #ccc matches what snippets/swatch.liquid paints for an unresolved swatch on
    // the PDP — see the longer note in assets/cart-drawer-edit-item.js.
    const name = String(colorValue || '').trim();

    return name && CSS.supports('color', name) ? name : '#ccc';
  }

  /**
   * Reads the Liquid-built swatch map, retrying case- and whitespace-insensitively so a colour saved
   * as `"Bone "` in the admin still matches the `"Bone"` the option list reports.
   *
   * @param {string} colorValue
   * @returns {string | {color?: string, image?: string} | undefined}
   */
  #lookupSwatch(colorValue) {
    const map = this.#productData?.swatchMap;

    if (!map) return undefined;
    if (map[colorValue] != null) return map[colorValue];

    const wanted = String(colorValue || '')
      .trim()
      .toLowerCase();

    if (!wanted) return undefined;

    const key = Object.keys(map).find((candidate) => candidate.trim().toLowerCase() === wanted);

    return key === undefined ? undefined : map[key];
  }

  async #addToCart() {
    if (!this.#selectedVariantId || this.#isAdding) {
      return;
    }

    this.#isAdding = true;
    const ctaBtn = this.querySelector('[ref="ctaBtn"]');
    const originalText = this.#ctaLabel;

    if (ctaBtn) {
      ctaBtn.classList.add('is-loading');
      ctaBtn.textContent = 'Adding...';
      ctaBtn.disabled = true;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: this.#selectedVariantId, quantity: 1 }],
          sections: [this.#getSectionIds()].filter(Boolean),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add item');
      }

      const data = await response.json();

      this.#closeNow();

      // Restore the label here too, not only in open() — if anything downstream ever keeps the
      // sheet on screen, the shopper must not be left staring at a button that says "Adding…" for
      // an add that already landed.
      if (ctaBtn) {
        ctaBtn.textContent = originalText;
      }

      await announceCartLinesUpdate({
        action: 'add',
        lines: [{ merchandiseId: String(this.#selectedVariantId), quantity: 1 }],
        source: 'cart-drawer-quick-add',
        sourceId: 'cart-drawer-quick-add',
        sections: data.sections,
        // `/cart/add.js` returns the added line items with their merged quantity — enough for
        // `detail.items`, so the announcement can resolve without a second round trip.
        items: data.items,
        variantId: String(this.#selectedVariantId),
      });
    } catch (error) {
      console.error('[cart-drawer-quick-add]', error.message);

      if (ctaBtn) {
        ctaBtn.textContent = 'Error';
        setTimeout(() => {
          ctaBtn.textContent = originalText;
          ctaBtn.disabled = false;
        }, 1500);
      }
    } finally {
      this.#isAdding = false;

      if (ctaBtn) {
        ctaBtn.classList.remove('is-loading');
      }
    }
  }

  /**
   * @returns {string}
   */
  #getSectionIds() {
    const cartItemsComponent = document.querySelector('cart-items-component[data-section-id]');

    if (cartItemsComponent) {
      return cartItemsComponent.dataset.sectionId || '';
    }

    return '';
  }

  /**
   * @param {MouseEvent} event
   */
  #handleDialogClick(event) {
    if (event.target === this.#dialog) {
      this.close();
    }
  }

  /**
   * @param {Event} event
   */
  #handleCancel(event) {
    event.preventDefault();
    this.close();
  }
}

if (!customElements.get('cart-drawer-quick-add')) {
  customElements.define('cart-drawer-quick-add', CartDrawerQuickAdd);
}
